import {
  Engagement,
  Feedback as FeedbackIcon,
  Goal as GoalIcon,
  HistoryComments as HistoryCommentsIcon,
  Initiatives as InitiativesIcon,
  MeshMavenWithHording as MeshMavenWithHordingIcon,
  OneOnOne as OneOnOneIcon,
  Reviews as ReviewsIcon,
  SelfScore as SelfScoreIcon,
} from '@mesh.ai/rabbit';
import { checkLevelUpFeatureFlagEnabled } from 'utils/helpers/featureFlags';

import { Chat, DisciplinaryActionsIconDualTone, Notifications } from 'assets/assetsV1/iconComponents';
import { RabbitEmail } from 'assets/assetsV1/iconComponents/RabbitEmail';
import BellNotificationImg from 'assets/images/bell_notifications.png';
import BrowserNotificationImg from 'assets/images/browser_notifications.png';
import EmailNotificationImg from 'assets/images/email_notifications.png';
import SlackNotificationImg from 'assets/images/slack_notifications.png';

import {
  ENGAGEMENT_ADMIN,
  FEEDBACK_ADMIN,
  GOAL_ADMIN,
  ONE_ON_ONE_ADMIN,
  PLATFORM_ADMIN,
  REVIEW_ADMIN,
  TASK_ADMIN,
} from 'constants/ProjectConstants';
import { BrowserNotificationIcon } from 'constants/icons';
import { isNetchexLogin } from 'constants/netChexConstants';
import theme from 'constants/theme';

export const NOTIFICATION_CHANNELS = {
  // FEED: {
  //   heading: 'Feed',
  //   description: 'Lorem ipsum dolor sit, amet consectetur adipisicing elit. ',
  //   img: FeedNotificationImg,
  // },
  BELL: {
    heading: 'Bell',
    description: 'The bell icon displays red badges for notifications, click it to view the most recent ones.',
    img: BellNotificationImg,
    order: 0,
  },
  EMAIL: {
    heading: 'Email',
    description: 'Email notifications will be sent to your inbox',
    img: EmailNotificationImg,
    order: 1,
  },
  BROWSER: {
    heading: 'Browser',
    description:
      'Browser notifications appear on your screen when you’re not active in Mesh, but the site is open in your browser tab or window.',
    img: BrowserNotificationImg,
    order: 4,
  },
  SLACK: {
    heading: 'Third party chat applications',
    description: 'These notifications will appear on connected third part chat applications e.g Slack. MS Teams',
    img: SlackNotificationImg,
    order: 2,
  },
};

export const NOTIFICATION_EVENT_CHANNELS = {
  BELL: {
    // @ts-expect-error ts doesn't like this icon
    icon: <Notifications boxSize="20px" color={theme.palette.icons.dark} fill="transparent" />,
    type: 'BELL',
    name: 'Alert',
  },
  EMAIL: {
    // @ts-expect-error ts doesn't like this icon
    icon: <RabbitEmail boxSize="20px" color={theme.palette.icons.dark} fill="transparent" />,
    type: 'EMAIL',
    name: 'Email',
  },
  SLACK: {
    // @ts-expect-error ts doesn't like this icon
    icon: <Chat boxSize="22px" color={theme.palette.icons.dark} fill="transparent" />,
    type: 'SLACK',
    name: 'Third party',
  },
  BROWSER: {
    // @ts-expect-error ts doesn't like this icon
    icon: <BrowserNotificationIcon size="fixed16px" color={theme.palette.icons.dark} />,
    type: 'BROWSER',
    name: 'Browser',
  },
};

export const GOAL_NOTIFICATION_EVENTS_META = {
  GOAL_PROGRESS_UPDATE: {
    heading: 'Goal progress',
    description: 'Get notified when there are updates in goal progress',
  },
  GOAL_CHECKIN_REMINDER: {
    heading: 'Check-in reminders',
    description: 'Get check-in reminders to update progress on your goals',
  },
  GOAL_DETAILS_UPDATE: {
    heading: 'Goal details',
    description: 'Get notified when there are updates in goal details',
  },
  GOAL_COMMENT: {
    heading: 'Goal comment',
    description: 'Get notified when someone comments on a goal in which you are marked as owner/collaborator',
  },
  GOAL_DELETION: {
    heading: 'Goal deletion',
    description: 'Get notified when someone deletes a goal in which you are marked as owner/collaborator',
  },
  GOAL_MONTHLY_DIGEST: {
    heading: 'Goal monthly digest',
    description: 'Monthly updates on goal module ',
  },
  GOAL_WEEKLY_DIGEST: {
    heading: 'Goal weekly digest',
    description: 'Weekly updates on goal module ',
  },
  GOAL_TASK: {
    heading: 'Goal task',
    description: 'Get notified when there are updates in goal task ',
  },
  displayName: 'Goals',
  order: 0,
  access: GOAL_ADMIN,
};

export const TASK_NOTIFICATION_EVENTS_META = {
  SUB_TASK: {
    heading: 'Task',
    description: 'Get notified on creation and updation of tasks ',
  },
  TASK_COMMENT: {
    heading: 'Initiative comment',
    description: 'Get notified when someone comments on a Initiative in which you are marked as owner/collaborator',
  },
  TASK_DELETION: {
    heading: 'Initiative deletion',
    description: 'Get notified when someone deletes a Initiative in which you are marked as owner/collaborator',
  },
  TASK_DETAILS_UPDATE: {
    heading: 'Initiative details update',
    description:
      'Get notified when there are updates in owner, collaborator, objective, description, start date and due date',
  },
  TASK_MONTHLY_DIGEST: {
    heading: 'Initiative monthly digest',
    description: 'Monthly updates on Initiative module ',
  },
  TASK_PROGRESS_UPDATE: {
    heading: 'Initiative progress update',
    description: 'Get notified when there are updates in status of a Initiative ',
  },
  TASK_WEEKLY_DIGEST: {
    heading: 'Initiative weekly digest',
    description: 'Weekly updates on Initiative module ',
  },
  displayName: 'Initiatives',
  order: 1,
  access: TASK_ADMIN,
};

export const ONE_ON_ONE_NOTIFICATION_EVENTS_META = {
  ACTION_ITEMS: {
    heading: 'Action items',
    description: 'Get notified on creation, updation and completion of action items',
  },
  AGENDA: {
    heading: 'Agenda',
    description: "Get notified on creation, updation and completion of meeting agenda's",
  },
  ONE_ON_ONE_COMPLETION: {
    heading: '1:1 completion',
    description: 'Get notified when a 1:1 is marked as complete',
  },
  ONE_ON_ONE_CREATION: {
    heading: '1:1 creation',
    description: 'Get notified when someone tags you in a 1:1',
  },
  ONE_ON_ONE_MONTHLY_DIGEST: {
    heading: '1:1 monthly digest',
    description: 'Monthly updates on 1:1 module ',
  },
  ONE_ON_ONE_WEEKLY_DIGEST: {
    heading: '1:1 weekly digest',
    description: 'Weekly updates on 1:1 module ',
  },
  SCHEDULE_MODIFICATION: {
    heading: '1:1 schedule modification',
    description: 'Get notified when there is a modification in 1:1 schedule ',
  },
  ONE_ON_ONE_REMINDER: {
    heading: '1:1 reminder',
    description: 'Get a reminder before a scheduled 1:1',
  },
  displayName: '1:1',
  order: 2,
  access: ONE_ON_ONE_ADMIN,
};

export const ONE_ON_ONE_NOTIFICATION_EVENTS_META_RABBIT = {
  OOO_CREATION: {
    heading: '1:1 creation',
    description: 'Get notified when someone tags you in a 1:1',
  },
  OOO_REMINDER: {
    heading: '1:1 reminder',
    description: 'Get a reminder 24 hours before your 1:1 starts',
  },
  OOO_SCHEDULE_MODIFICATION: {
    heading: '1:1 schedule modification',
    description: 'Get notified when any modification happens to existing 1:1 instance or the 1:1 schedule',
  },
  OOO_COMPLETION_SUMMARY: {
    heading: '1:1 summary',
    description: 'Receive a summary of your 1:1 via e-mail once it is complete',
  },
  displayName: '1:1',
  order: 2,
  access: ONE_ON_ONE_ADMIN,
};

export const REVIEW_NOTIFICATION_EVENTS_META = {
  FEEDBACK_REQUEST: {
    heading: 'Share or request feedback',
    description: 'Get notified when someone requests or shares a feedback',
  },
  REVIEW_MONTHLY_DIGEST: {
    heading: 'Review monthly digest',
    description: 'Monthly updates on review module',
  },
  REVIEW_REQUEST_UPDATE: {
    heading: 'Review request',
    description:
      'Get notified when there are updates in peer nominations, self review, manager review and upward review',
  },
  REVIEW_WEEKLY_DIGEST: {
    heading: 'Review weekly digest',
    description: 'Weekly updates on review module',
  },
  REVIEW_CYCLE_REMINDER: {
    isNotControlledByNotificationSettings: true,
    heading: 'Review cycle reminders',
    description: 'Get reminders on the pending review form',
    message: 'Configured via individual review cycles',
  },
  displayName: 'Reviews',
  order: 3,
  access: REVIEW_ADMIN,
};

export const ENGAGEMENT_NOTIFICATION_EVENTS_META = {
  ENGAGEMENT_MONTHLY_DIGEST: {
    heading: 'Engagement monthly digest',
    description: 'Monthly updates on engagement module ',
  },
  ENGAGEMENT_REQUEST_UPDATE: {
    heading: 'Engagement request update',
    description: 'Monthly updates on engagement module ',
  },
  ENGAGEMENT_WEEKLY_DIGEST: {
    heading: 'Engagement weekly digest',
    description: 'Weekly updates on engagement module ',
  },
  ENGAGEMENT_REMINDER: {
    isNotControlledByNotificationSettings: true,
    heading: 'Engagement reminders',
    description: 'Get reminders on the pending engagement survey',
    message: 'Configured via individual engagement surveys',
  },
  displayName: 'Engagements',
  order: 4,
  access: ENGAGEMENT_ADMIN,
};

export const FEEDBACK_NOTIFICATION_EVENTS_META = {
  FEEDBACK_REQUEST: {
    heading: 'Share or request feedback',
    description: 'Get notified when someone requests or shares a feedback',
  },
  displayName: 'Feedback',
  order: 6,
  access: FEEDBACK_ADMIN,
};

export const POST_NOTIFICATION_EVENTS_META = {
  USER_CREATED_POST: {
    heading: 'Post creation',
    description: 'Get notified when someone mentions you in a feed post',
  },
  USER_REACTION: {
    heading: 'Post reaction',
    description: 'Get notified when someone comments on a feed post related to you or mentions you in a post comment',
  },
  displayName: 'Feed post',
  order: 7,
  access: FEEDBACK_ADMIN,
};

export const MESH_PLATFORM_NOTIFICATION_EVENTS_META = {
  TIPS_HOW_TOS_NOTIFICATION: {
    heading: 'Tips and how-tos',
    description: 'Occasional notifications to help you get most out of Mesh',
    order: 0,
  },
  MESH_RECOMMENDS: {
    heading: 'Mesh recommends',
    description:
      'Using people and data science-backed nudges to assist individuals in determining their next best actions',
    order: 1,
  },
  PRODUCT_UPDATE_NOTIFICATION: {
    heading: 'Product updates',
    description: 'Occasional notifications on new features and product updates',
    order: 2,
  },
  PRODUCT_RESEARCH_NOTIFICATION: {
    isNotControlledByNotificationSettings: true,
    heading: 'Product research',
    description: 'Occasional surveys to help us improve Mesh',
    order: 3,
  },
  DIGEST_NOTIFICATION: {
    isNotControlledByNotificationSettings: true,
    heading: 'Digests',
    description: 'Weekly/Monthly summary of your and your team activities',
    order: 4,
  },
  displayName: 'Mesh platform',
  order: 8,
  access: PLATFORM_ADMIN,
};

export const LEVEL_UP_EVENTS_META = {
  LEVEL_UP_CHECK_IN: {
    heading: 'Competency progress',
    description:
      "Get notified when progress has been updated for your competency or for your direct reports' competency",
    order: 1,
  },
  LEVEL_UP_COMMENT: {
    heading: 'Competency comments',
    description:
      "Get notified when a comment has been added for your competency or for your direct reports' competency",
    order: 2,
  },
  LEVEL_UP_DETAILS: {
    heading: 'Competency details',
    description:
      "Get notified when initiatives are created/deleted or target quarter has been set for your competency or for your direct reports' competency",
    order: 3,
  },
  LEVEL_UP_TARGET_ROLE: {
    heading: 'Target role selected',
    description:
      "Get notified when you are assigned a target role or when your direct reports' choose their target role",
    order: 0,
  },
  displayName: 'Develop',
  order: 5,
  access: FEEDBACK_ADMIN,
  featureFlag: checkLevelUpFeatureFlagEnabled,
};

export const CATEGORIZED_NOTIFICATION_EVENTS = [
  {
    name: 'New user onboarding',
    description: 'Helps you discover key features and make most of your experience',
    icon: SelfScoreIcon,
    events: ['NEW_USER_ONBOARDING'],
  },
  {
    name: 'Goals notifications',
    description: 'Stay informed on updates and progress for your performance Goals',
    icon: GoalIcon,
    events: ['GOAL_PLANNING', 'GOAL_CHECKIN', 'GOAL_COMPLETION', ...(isNetchexLogin ? ['TAGGED_IN_COMMENTS'] : [])],
  },
  {
    name: 'Feedback notifications',
    description: 'Receive updates on feedback and praise shared across your team',
    icon: FeedbackIcon,
    events: ['FEEDBACKS'],
  },
  {
    name: '1-1 notifications',
    description: 'Keep track of your 1:1 meetings, from scheduling to completion',
    icon: OneOnOneIcon,
    events: ['ONE_ON_ONE_SCHEDULE', 'ONE_ON_ONE_COMPLETION'],
  },
  {
    name: 'Reviews notifications',
    description: 'Receive notifications about your progress in performance review cycles',
    icon: ReviewsIcon,
    events: [
      'REVIEW_COMPLETION_WORKFLOW',
      'REVIEW_CALIBRATION',
      'REVIEW_PUBLISH',
      ...(isNetchexLogin ? ['REVIEW_ACTION_PLAN'] : []),
    ],
  },
  {
    name: 'Initiatives notifications',
    description: 'Receive notifications for updates on your initiatives',
    icon: InitiativesIcon,
    events: ['TASK_PLANNING', 'TASK_CHECKIN'],
  },
  {
    name: 'Maven recommends',
    description: 'Get smart recommendations to enhance your performance journey.',
    icon: MeshMavenWithHordingIcon,
    events: ['MESH_RECOMMENDS', 'REVIEW_ACTION_PLAN'],
  },
  ...(isNetchexLogin
    ? []
    : [
        {
          name: 'Tagged in comments',
          description: 'Receive notifications when you are tagged in a comment or post',
          icon: HistoryCommentsIcon,
          events: ['TAGGED_IN_COMMENTS'],
        },
      ]),
  {
    name: 'Disciplinary actions',
    description: 'Manage notifications for disciplinary note workflows and approval processes',
    icon: () => <DisciplinaryActionsIconDualTone boxSize="18px" width="24px" />,
    events: ['DISCIPLINARY_ACTION'],
  },
  {
    name: 'Surveys',
    description: 'Stay informed about survey completion reminders and updates',
    icon: Engagement,
    events: ['ENGAGEMENT_REMINDER', 'ENGAGEMENT_COMPLETION'],
  },
];
